/**
 * PublicSharePage — publicly accessible evaluation result card at /share/:token.
 *
 * This route is registered OUTSIDE the AuthGuard wrapper so no login is required.
 * The page calls GET /api/share/:token via a plain fetch (no auth interceptor)
 * and renders a privacy-safe summary of the evaluation.
 *
 * States handled:
 *  - Loading skeleton while fetching
 *  - 410 Gone: expired or revoked link → user-friendly message
 *  - 404 Not Found: invalid token → user-friendly message
 *  - Success: branded result card with CTA
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicShare, PublicShareError } from '@/services/shareApi';
import type { PublicShareCard } from '@/services/shareApi';
import { Skeleton, SkeletonText } from '@/components/ui';
import wordmark from '@/assets/branding/wordmark.png';

// ─── Score helpers ────────────────────────────────────────────────────────────

function normalizeScore(score: number): number {
  return score > 1 ? Math.round(score) : Math.round(score * 100);
}

type Band = 'good' | 'warn' | 'bad';

function scoreBand(n: number): Band {
  if (n >= 80) return 'good';
  if (n >= 60) return 'warn';
  return 'bad';
}

const BAND_COLOR: Record<Band, string> = {
  good: 'var(--color-signal-good, #34D399)',
  warn: 'var(--color-signal-warn, #FBBF24)',
  bad: 'var(--color-signal-bad, #F87171)',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Score ring (SVG) ────────────────────────────────────────────────────────

const RING = { size: 140, stroke: 9 };
const RADIUS = (RING.size - RING.stroke) / 2;
const CIRCUMFERENCE = RADIUS * 2 * Math.PI;

interface ScoreRingProps {
  score: number;
  color: string;
}

function ScoreRing({ score, color }: ScoreRingProps) {
  const offset = CIRCUMFERENCE - (score / 100) * CIRCUMFERENCE;
  return (
    <svg
      width={RING.size}
      height={RING.size}
      viewBox={`0 0 ${RING.size} ${RING.size}`}
      aria-label={`Score ${score} out of 100`}
      role="img"
    >
      {/* Track */}
      <circle
        cx={RING.size / 2}
        cy={RING.size / 2}
        r={RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth={RING.stroke}
      />
      {/* Progress arc */}
      <circle
        cx={RING.size / 2}
        cy={RING.size / 2}
        r={RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={RING.stroke}
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${RING.size / 2} ${RING.size / 2})`}
        style={{ transition: 'stroke-dashoffset 0.8s ease' }}
      />
      {/* Score text */}
      <text
        x="50%"
        y="46%"
        dominantBaseline="middle"
        textAnchor="middle"
        fill={color}
        fontSize="28"
        fontWeight="700"
        fontFamily="var(--font-heading, sans-serif)"
      >
        {score}
      </text>
      <text
        x="50%"
        y="66%"
        dominantBaseline="middle"
        textAnchor="middle"
        fill="rgba(255,255,255,0.4)"
        fontSize="11"
        fontFamily="var(--font-body, sans-serif)"
      >
        /100
      </text>
    </svg>
  );
}

// ─── Sub-score pill ───────────────────────────────────────────────────────────

interface SubScorePillProps {
  label: string;
  score: number;
}

function SubScorePill({ label, score }: SubScorePillProps) {
  const band = scoreBand(score);
  const color = BAND_COLOR[band];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '2px',
        padding: '10px 18px',
        borderRadius: '12px',
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        minWidth: '90px',
      }}
    >
      <span style={{ fontSize: '22px', fontWeight: 700, color, fontFamily: 'var(--font-heading, sans-serif)' }}>
        {score}
      </span>
      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div style={{ maxWidth: '520px', margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Skeleton className="h-8 w-40 mx-auto" />
      <div
        style={{
          borderRadius: '20px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: '32px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <Skeleton className="h-[140px] w-[140px] rounded-full" />
        <Skeleton className="h-6 w-48" />
        <SkeletonText lines={3} />
      </div>
    </div>
  );
}

// ─── Error state ─────────────────────────────────────────────────────────────

interface ErrorCardProps {
  status: 404 | 410;
}

function ErrorCard({ status }: ErrorCardProps) {
  const is410 = status === 410;
  return (
    <div
      style={{
        maxWidth: '520px',
        margin: '0 auto',
        padding: '24px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '16px',
          backgroundColor: is410 ? 'rgba(251,191,36,0.12)' : 'rgba(248,113,113,0.12)',
          border: `1px solid ${is410 ? 'rgba(251,191,36,0.3)' : 'rgba(248,113,113,0.3)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
        }}
        aria-hidden="true"
      >
        {is410 ? '⏱' : '?'}
      </div>
      <div>
        <p
          style={{
            margin: '0 0 8px',
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--color-text-primary, #fff)',
            fontFamily: 'var(--font-heading, sans-serif)',
          }}
          data-testid="share-error-heading"
        >
          {is410 ? 'Link has expired' : 'Link not found'}
        </p>
        <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
          {is410
            ? 'This share link has expired or been revoked. Contact the person who shared it to request a new link.'
            : 'This share link is not valid. It may have been mistyped or does not exist.'}
        </p>
      </div>
      <Link
        to="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '10px 22px',
          borderRadius: '999px',
          backgroundColor: 'var(--color-accent, #1ED760)',
          color: '#0B0F14',
          fontSize: '14px',
          fontWeight: 700,
          textDecoration: 'none',
          fontFamily: 'var(--font-heading, sans-serif)',
        }}
      >
        Go to PointeTracer
      </Link>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PublicSharePage() {
  const { token } = useParams<{ token: string }>();
  const [card, setCard] = useState<PublicShareCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState<404 | 410 | null>(null);

  useEffect(() => {
    document.title = 'Evaluation Result — PointeTracer';
  }, []);

  useEffect(() => {
    if (!token) {
      setErrorStatus(404);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setErrorStatus(null);

    getPublicShare(token)
      .then((data) => {
        if (!cancelled) {
          setCard(data);
          // Update page title with athlete name and template once data arrives.
          const name = data.display_name ? `${data.display_name} — ` : '';
          document.title = `${name}${data.template_name} · PointeTracer`;
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err instanceof PublicShareError) {
            setErrorStatus(err.status);
          } else {
            // Unexpected error — treat as 404 for the user.
            setErrorStatus(404);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  // ── Page shell (branded, no sidebar) ────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-dark-base, #0B0F14)',
        color: 'var(--color-text-primary, #fff)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}
          aria-label="PointeTracer home"
        >
          <img
            src={wordmark}
            alt="PointeTracer"
            style={{ height: '24px', width: 'auto', objectFit: 'contain' }}
          />
        </Link>
        <Link
          to="/register"
          style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.55)',
            textDecoration: 'none',
            fontFamily: 'var(--font-body, sans-serif)',
          }}
        >
          Sign up free
        </Link>
      </header>

      {/* Content */}
      <main style={{ flex: 1, padding: '40px 16px 60px' }}>
        {isLoading && <LoadingSkeleton />}

        {!isLoading && errorStatus && <ErrorCard status={errorStatus} />}

        {!isLoading && !errorStatus && card && <ShareResultCard card={card} />}
      </main>

      {/* Footer CTA */}
      {!isLoading && !errorStatus && card && (
        <footer
          style={{
            padding: '24px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
            Powered by PointeTracer — AI movement analysis for coaches and athletes
          </p>
          <Link
            to="/register"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 24px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.18)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
              fontFamily: 'var(--font-heading, sans-serif)',
              transition: 'border-color 150ms ease, color 150ms ease',
            }}
            data-testid="share-cta-signup"
          >
            Get evaluated on PointeTracer
          </Link>
        </footer>
      )}
    </div>
  );
}

// ─── Result card ─────────────────────────────────────────────────────────────

interface ShareResultCardProps {
  card: PublicShareCard;
}

function ShareResultCard({ card }: ShareResultCardProps) {
  const overall = normalizeScore(card.overall_score);
  const band = scoreBand(overall);
  const color = BAND_COLOR[band];

  const formScore = card.form_score != null ? normalizeScore(card.form_score) : null;
  const timingScore = card.timing_score != null ? normalizeScore(card.timing_score) : null;

  return (
    <div
      style={{
        maxWidth: '520px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
      data-testid="share-result-card"
    >
      {/* Meta header */}
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
          Movement evaluation
        </p>
        <h1
          style={{
            margin: 0,
            fontSize: '22px',
            fontWeight: 700,
            color: '#fff',
            fontFamily: 'var(--font-heading, sans-serif)',
            lineHeight: 1.3,
          }}
          data-testid="share-template-name"
        >
          {card.template_name}
        </h1>
        {card.display_name && (
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'rgba(255,255,255,0.55)' }}>
            by {card.display_name}
          </p>
        )}
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
          {formatDate(card.created_at)}
        </p>
      </div>

      {/* Main score card */}
      <div
        style={{
          borderRadius: '20px',
          background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
          border: `1px solid rgba(255,255,255,0.09)`,
          boxShadow: `0 0 40px ${color}18`,
          padding: '32px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        {/* Score ring */}
        <ScoreRing score={overall} color={color} />

        {/* Sub-scores row */}
        {(formScore != null || timingScore != null) && (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {formScore != null && <SubScorePill label="Form" score={formScore} />}
            {timingScore != null && <SubScorePill label="Timing" score={timingScore} />}
          </div>
        )}

        {/* Coaching summary */}
        {card.coaching_summary && (
          <div
            style={{
              padding: '16px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.07)',
              width: '100%',
            }}
          >
            <p
              style={{
                margin: '0 0 6px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'rgba(255,255,255,0.35)',
              }}
            >
              AI Coaching Summary
            </p>
            <p
              style={{
                margin: 0,
                fontSize: '14px',
                color: 'rgba(255,255,255,0.78)',
                lineHeight: 1.65,
              }}
              data-testid="share-coaching-summary"
            >
              {card.coaching_summary}
            </p>
          </div>
        )}
      </div>

      {/* Video player (only when allow_video=true on the share) */}
      {card.video_url && (
        <div
          style={{
            borderRadius: '14px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: '#000',
          }}
        >
          <video
            src={card.video_url}
            controls
            playsInline
            preload="metadata"
            style={{ width: '100%', display: 'block', maxHeight: '480px', objectFit: 'contain' }}
            aria-label="Athlete evaluation video"
          />
        </div>
      )}
    </div>
  );
}
